import { apiClient } from './client'

export interface CurrentUser {
  authenticated: boolean
  message?: string
  id?: string
  email?: string
  name?: string
  role?: 'viewer' | 'responder' | 'admin' | 'owner'
  ssoEnabled?: boolean
  // Which organization this session acts in (REG-271). Optional: the
  // backend omits it rather than failing /auth/me when the org cannot be
  // resolved, and the SAML-fallback branch has no org to report.
  org?: {
    id: string
    name: string
    slug: string
    // False until an OAuth-signup owner confirms or changes the default name
    // (REG-285). Absent from an older backend, which never triggers the prompt.
    name_confirmed?: boolean
  }
}

export interface LoginRequest {
  email: string
  password: string
}

export async function getCurrentUser(): Promise<CurrentUser> {
  return apiClient.get<CurrentUser>('/api/v1/auth/me')
}

export interface OrgOption {
  id: string
  name: string
  slug: string
}

export interface LoginResult {
  // True when the password was correct but a second factor is still
  // required. The session cookie is NOT set in that case.
  mfa_required?: boolean
  // True when the credential matched more than one organization (REG-273).
  // Never set together with mfa_required: MFA is checked only once an org
  // is chosen, so select-org can itself answer mfa_required.
  org_selection_required?: boolean
  pending_token?: string
  orgs?: OrgOption[]
}

export type LoginStep = 'mfa' | 'pick-org' | 'done'

// What the page does next with a login or select-org answer. A flag without
// its token is not actionable, so it counts as done rather than stranding
// the user on a step that cannot complete.
export function loginStepFor(res: LoginResult | undefined): LoginStep {
  if (res?.mfa_required && res.pending_token) return 'mfa'
  if (res?.org_selection_required && res.pending_token) return 'pick-org'
  return 'done'
}

export async function login(req: LoginRequest): Promise<LoginResult> {
  return apiClient.post<LoginResult>('/api/v1/auth/login', req)
}

export async function completeMFALogin(pendingToken: string, code: string): Promise<void> {
  await apiClient.post<{ ok: boolean }>('/api/v1/auth/login/mfa', {
    pending_token: pendingToken,
    code,
  })
}

// Second step of a multi-org login (REG-273). Returns a LoginResult, not
// void: the chosen org may have MFA, in which case this answers
// mfa_required and a pending token for the MFA step instead of a session.
export async function selectOrg(pendingToken: string, orgId: string): Promise<LoginResult> {
  return apiClient.post<LoginResult>('/api/v1/auth/login/select-org', {
    pending_token: pendingToken,
    org_id: orgId,
  })
}

export interface LoginDiscovery {
  method: 'saml' | 'password'
  redirect?: string
}

// Email-first login (REG-276): the server says whether this address's
// company signs in with SSO. Every other case is 'password'.
export async function discoverLogin(email: string): Promise<LoginDiscovery> {
  return apiClient.post<LoginDiscovery>('/api/v1/auth/login/discover', { email })
}

const SSO_PATH = /^\/saml\/o\/[0-9a-f-]{36}\/login$/

// The ?sso= parameter comes back in a URL, so it is attacker-controllable:
// only a per-org SAML login path on this site may be followed.
export function isSafeSSOPath(path: string): boolean {
  return SSO_PATH.test(path)
}

// Org names for a picker reached by redirect (OAuth), read without spending
// the pending token.
export async function getPendingOrgs(token: string): Promise<{ orgs: OrgOption[] }> {
  const res = await apiClient.get<{ orgs?: OrgOption[] }>(
    `/api/v1/auth/login/pending-orgs?token=${encodeURIComponent(token)}`
  )
  return { orgs: res.orgs ?? [] }
}

export async function exchangeSetupToken(token: string): Promise<void> {
  return apiClient.post<void>('/api/v1/auth/login/setup-token', { token })
}

// completePasswordReset sets a new password and logs in with the same
// token in one call. Unlike exchangeSetupToken, the token here never logs
// anyone in on its own -- only a successful password change does.
export async function completePasswordReset(token: string, newPassword: string): Promise<void> {
  await apiClient.post<{ ok: boolean }>('/api/v1/auth/reset-password/complete', {
    token,
    new_password: newPassword,
  })
}

export async function logout(): Promise<void> {
  await apiClient.post<unknown>('/api/v1/auth/logout')
}

export async function updateMe(req: { name?: string; current_password?: string; new_password?: string }): Promise<void> {
  await apiClient.patch<unknown>('/api/v1/auth/me', req)
}

// REG-181: the response is deliberately constant and never carries the
// reset token -- the token authenticates directly on redemption, so it is
// emailed to the address instead. There is nothing to return.
export async function forgotPassword(email: string, returnTo?: string): Promise<void> {
  await apiClient.post<{ ok: boolean }>('/api/v1/auth/forgot-password', {
    email,
    ...(returnTo ? { return_to: returnTo } : {}),
  })
}

// REG-178: the response is deliberately constant and carries nothing about
// whether the address matched, so there is nothing to return.
export async function findWorkspace(email: string, returnTo?: string): Promise<void> {
  await apiClient.post<{ ok: boolean }>('/api/v1/auth/find-workspace', {
    email,
    ...(returnTo ? { return_to: returnTo } : {}),
  })
}

const LOCKOUT_CODES = new Set(['account_locked', 'mfa_locked', 'invalid_pending_token'])

// A lockout or rate limit is not a wrong password: telling someone their
// correct password is "invalid" sends them to reset it for nothing.
export function loginErrorMessage(err: unknown, fallback: string): string {
  const body = (err as { response?: { error?: unknown } } | null)?.response
  const detail = body?.error
  if (detail && typeof detail === 'object') {
    const { code, message } = detail as { code?: string; message?: string }
    if (code && LOCKOUT_CODES.has(code) && message) return message
  }
  if (detail === 'rate limit exceeded') {
    return 'Too many attempts from this network. Wait a minute and try again.'
  }
  return fallback
}

// REG-284: shown when Google/GitHub sign-in is refused because the person's
// workspace turned it off (for example after connecting company SSO).
export const SOCIAL_DISABLED_MESSAGE =
  'Google and GitHub sign-in is turned off for your workspace. Sign in with your company single sign-on or your password, or ask your administrator.'

export function apiErrorCode(err: unknown): string | undefined {
  const detail = (err as { response?: { error?: unknown } } | null)?.response?.error
  if (detail && typeof detail === 'object') return (detail as { code?: string }).code
  return undefined
}
